import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActionMeta, GroupBase, SingleValue } from 'react-select';
import styled from 'styled-components';

import { ColorsV2, SearchAsyncSelect, Text } from '@electricjs/arc';

import { useLazyGetOrganizationEmployeeDirectoryQuery } from '@/redux/slices/organizationApiSlice';

import { searchResultCustomComponents } from './components/SearchResultCustomComponents';
import { type SmartSearchSelectOption } from './resultKindConfig';
import { search } from './utils/search';

const StyledSearchAsyncSelect = styled(SearchAsyncSelect)`
  [class$='-control'] {
    z-index: 2;
    height: 5rem;
    /* Pulls the menu in close to the search bar */
    margin-bottom: -0.3rem;
  }
  [class$='-menu'] {
    /* Makes the menu appear to sit under the search */
    z-index: 1;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    width: calc(100% - 4px);
    margin-left: 2px;
  }
`;

const formatGroupLabel = (optionGroup: GroupBase<SmartSearchSelectOption>) => {
  return (
    <Text variant="legal" color={ColorsV2.GRAY_DARK}>
      {optionGroup.label}
    </Text>
  );
};

type SmartSearchProps = {
  searchableData: SmartSearchSelectOption[];
  isAdmin: boolean;
  organizationId: string;
  isInsideModal?: boolean;
};

// Handles navigating to the URL for the focused option when the user presses enter or clicks on an option
const onSelectOption = (
  selectedOption: SingleValue<SmartSearchSelectOption>,
  { action }: ActionMeta<SmartSearchSelectOption>
) => {
  if (action === 'select-option' && selectedOption) {
    window.location.href = selectedOption.url;
  }
};

const SmartSearch = ({
  searchableData,
  isAdmin,
  organizationId,
  isInsideModal,
}: SmartSearchProps) => {
  const { t } = useTranslation('common');
  const [getOrganizationEmployees] =
    useLazyGetOrganizationEmployeeDirectoryQuery();

  // Create employee fetcher function with consistent caching
  const employeesFetcher = useMemo(() => {
    return async (searchTerm: string) => {
      // Trim the search term once for consistency
      const trimmedTerm = searchTerm.trim();

      // Return empty array for empty or whitespace-only searches
      if (!trimmedTerm) return [];

      try {
        // Use consistent parameters for better caching
        const queryArgs = {
          organizationId,
          term: trimmedTerm,
        };

        const response = await getOrganizationEmployees(queryArgs).unwrap();
        return response || [];
      } catch (error) {
        console.error('Error fetching employees:', error);
        return [];
      }
    };
  }, [getOrganizationEmployees, organizationId]);

  // Create search function that returns groups for SearchAsyncSelect
  const handleSearch = async (searchTerm: string) => {
    const searchResults = await search(
      searchableData,
      searchTerm,
      isAdmin,
      employeesFetcher
    );
    return searchResults;
  };

  const defaultOptions = searchableData.filter(item => item.isDefaultOption);

  const defaultGroup = {
    label: t('smartSearch.suggestedPages'),
    options: defaultOptions,
  };

  return (
    // @ts-expect-error Arc's SearchAsyncSelect types don't fully support grouped options, but react-select does underneath
    <StyledSearchAsyncSelect
      id="global-smart-search-select"
      defaultOptions={[defaultGroup]}
      fetchOptions={handleSearch}
      debounceDelay={300}
      loadingText={t('smartSearch.lookingText')}
      placeholder={t('smartSearch.placeholder')}
      customDropdownIndicator="search"
      isSearchable
      maxMenuHeight={700}
      customComponents={searchResultCustomComponents}
      controlShouldRenderValue={false} // Prevents the selected value from being rendered in the search box
      isOptionSelected={() => false} // Prevents options from being "selected"
      formatGroupLabel={formatGroupLabel}
      onChange={onSelectOption}
      defaultMenuIsOpen={isInsideModal}
      menuIsOpen={isInsideModal}
    />
  );
};

export default SmartSearch;
